// src/pages/Demo.jsx — Public demo landing page at app.revhapi.com/demo
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  BarChart3, ShoppingCart, TrendingUp, Users, Zap, Check,
  ArrowRight, Loader2, Play,
} from 'lucide-react';

const ROLES = ['Founder / Owner', 'Marketer', 'Ecommerce Manager', 'Publisher', 'Agency', 'Developer', 'Other'];
const USE_CASES = ['Track ad spend & ROAS', 'Shopify P&L analytics', 'Publisher revenue', 'Multi-platform attribution', 'Agency reporting'];
const TEAM_SIZES = ['Just me', '2–5', '6–20', '20+'];

const DEMO_FEATURES = [
  { icon: ShoppingCart, label: '9,000+ Shopify orders', color: 'text-green-600', bg: 'bg-green-50' },
  { icon: BarChart3,    label: 'Meta, Google & TikTok ads', color: 'text-blue-600',  bg: 'bg-blue-50'  },
  { icon: TrendingUp,   label: 'Full P&L dashboard',        color: 'text-purple-600',bg: 'bg-purple-50'},
  { icon: Users,        label: 'CLV & cohort analytics',    color: 'text-orange-600',bg: 'bg-orange-50'},
  { icon: Zap,          label: 'Landing pages & A/B tests', color: 'text-yellow-600',bg: 'bg-yellow-50'},
];

export default function Demo() {
  const navigate = useNavigate();

  const [step, setStep]       = useState(1); // 1 = survey, 2 = loading, 3 = error
  const [form, setForm]       = useState({ name: '', email: '', company: '', role: '', use_case: '', team_size: '' });
  const [errors, setErrors]   = useState({});
  const [loadingMsg, setLoadingMsg] = useState('');
  const [errMsg, setErrMsg]   = useState('');
  const [debug, setDebug]     = useState([]);

  const log = (msg, data) => {
    const entry = `[${new Date().toISOString().slice(11,23)}] ${msg}${data ? ' → ' + JSON.stringify(data) : ''}`;
    console.log(entry);
    setDebug(prev => [...prev, entry]);
  };

  const validate = () => {
    const e = {};
    if (!form.email.trim())                     e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStep(2);
    setDebug([]);

    const messages = [
      'Saving your preferences…',
      'Loading 9,000+ orders…',
      'Preparing your dashboard…',
      'Almost ready…',
    ];
    let i = 0;
    setLoadingMsg(messages[0]);
    const interval = setInterval(() => {
      i++;
      if (i < messages.length) setLoadingMsg(messages[i]);
    }, 900);

    try {
      // Step 1: Save the lead via API (fire and check)
      log('Saving lead', { email: form.email });
      const res = await fetch('/api/demo-login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      });
      log('API response', { status: res.status });

      // Step 2: Sign in directly with Supabase client — bypasses all lock issues
      log('Signing in with Supabase client…');
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email:    process.env.REACT_APP_DEMO_EMAIL    || 'demo@revhapi.com',
        password: process.env.REACT_APP_DEMO_PASSWORD || 'revhapi-demo-2025',
      });

      log('Auth result', { user: authData?.user?.email, error: authError?.message });

      clearInterval(interval);

      if (authError) {
        setErrMsg(`Sign-in failed: ${authError.message}`);
        setStep(3);
        return;
      }

      log('Signed in — redirecting to dashboard');
      window.location.href = '/dashboard';

    } catch (err) {
      clearInterval(interval);
      log('Exception', { message: err.message });
      setErrMsg(`Error: ${err.message}`);
      setStep(3);
    }
  };

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (step === 2) return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="relative mx-auto mb-6 h-16 w-16">
          <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <BarChart3 className="h-7 w-7 text-primary" />
          </div>
        </div>
        <h2 className="text-xl font-bold mb-2">Setting up your demo</h2>
        <p className="text-muted-foreground text-sm mb-6">{loadingMsg}</p>
        <div className="w-full bg-border rounded-full h-1.5 overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '70%' }} />
        </div>
      </div>
    </div>
  );

  // ── Error screen ───────────────────────────────────────────────────────────
  if (step === 3) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <p className="text-red-600 font-medium mb-3">{errMsg}</p>
          <button onClick={() => { setStep(1); setDebug([]); }} className="text-primary hover:underline text-sm">← Try again</button>
        </div>
        {/* DEBUG PANEL — remove before launch */}
        {debug.length > 0 && (
          <div className="bg-gray-900 rounded-xl p-4 text-xs font-mono">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 font-semibold">Debug log</span>
              <button onClick={() => navigator.clipboard?.writeText(debug.join('\n'))}
                className="text-gray-500 hover:text-gray-300 text-xs px-2 py-0.5 border border-gray-700 rounded">
                Copy
              </button>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {debug.map((line, i) => (
                <div key={i} className={`leading-relaxed break-all ${
                  line.includes('error') || line.includes('Error') ? 'text-red-400' :
                  line.includes('Session set') || line.includes('success') ? 'text-green-400' :
                  'text-gray-300'
                }`}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Survey page ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background">

      {/* Nav */}
      <div className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">R</div>
            <span className="text-xl font-bold">RevHapi</span>
          </Link>
          <Link to="/register" className="text-sm text-muted-foreground hover:text-foreground">
            Create free account →
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">

          {/* Left: what's in the demo */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-5">
              <Play className="h-3 w-3" /> Live interactive demo
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight">
              See RevHapi with real data — no setup required
            </h1>
            <p className="text-muted-foreground text-lg mb-8">
              Explore a fully loaded account with 6 months of ecommerce data, live ad spend across 3 platforms, and publisher revenue. Everything is interactive.
            </p>

            <div className="space-y-3 mb-8">
              {DEMO_FEATURES.map(({ icon: Icon, label, color, bg }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${bg} flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <span className="text-sm font-medium">{label}</span>
                  <Check className="h-4 w-4 text-green-500 ml-auto flex-shrink-0" />
                </div>
              ))}
            </div>

            <div className="bg-accent rounded-xl p-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Demo data only</span> — this is a shared read-only account. No real stores or ad accounts are connected.
            </div>
          </div>

          {/* Right: survey form */}
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold mb-1">Access the live demo</h2>
            <p className="text-muted-foreground text-sm mb-6">Quick intro — takes 30 seconds</p>

            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Name + Email */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">First name</label>
                  <input
                    type="text"
                    placeholder="Alex"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Work email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    placeholder="alex@company.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className={`w-full px-3 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.email ? 'border-red-400' : 'border-border'}`}
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Company / store name</label>
                <input
                  type="text"
                  placeholder="Acme Store"
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">What's your role?</label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map(r => (
                    <button key={r} type="button" onClick={() => setForm(f => ({ ...f, role: r }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.role === r ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              {/* Use case */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">What brings you here?</label>
                <div className="flex flex-wrap gap-2">
                  {USE_CASES.map(u => (
                    <button key={u} type="button" onClick={() => setForm(f => ({ ...f, use_case: u }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.use_case === u ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                      {u}
                    </button>
                  ))}
                </div>
              </div>

              {/* Team size */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">Team size</label>
                <div className="flex gap-2">
                  {TEAM_SIZES.map(t => (
                    <button key={t} type="button" onClick={() => setForm(f => ({ ...f, team_size: t }))}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.team_size === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <button type="submit"
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors mt-2">
                Access the live demo <ArrowRight className="h-4 w-4" />
              </button>

              <p className="text-center text-xs text-muted-foreground">
                No credit card. No account needed. Instant access.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
